/**
 * Validates if a string is a properly formatted email address.
 * Uses a simple regex to check for the general structure of an email (local@domain.tld).
 * Pure export so non-component code (services, plain modules) can share the single regex.
 *
 * @param email - The email address string to validate.
 * @returns True if the email is valid, false otherwise.
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export default function () {
    const truncateText = (content: string, max?: number) => {
        if (!content) return;
        const maxNum = max || 30;
        if (content.length > maxNum) {
            return content.slice(0, maxNum) + '...';
        } else return content;
    };

    /**
     * Converts a snake_case or kebab-case string to Title Case with spaces.
     * Example: 'data_broker' => 'Data Broker'
     * @param str The string to convert
     */
    const toTitleCase = (str: string): string => {
        if (!str) return '';
        return str.replace(/[_-]+/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    };

    /**
     * Formats a number with commas for thousands separators.
     * Example: 1234567 => "1,234,567"
     * @param num The number to format
     * @returns The formatted number string
     */
    const formatNumber = (num: number | string | null | undefined): string => {
        if (num === null || num === undefined || num === '') return '0';

        const number = typeof num === 'string' ? parseFloat(num) : num;

        if (isNaN(number)) return '0';

        return number.toLocaleString('en-US');
    };

    /**
     * Formats minutes into days, hours, and minutes format.
     * Example: 3003 => "2d 5h 3m"
     * @param minutes The number of minutes to format
     * @returns The formatted time string
     */
    const formatTimeFromMinutes = (minutes: number | string | null | undefined): string => {
        if (minutes === null || minutes === undefined || minutes === '') return '0m';

        const totalMinutes = typeof minutes === 'string' ? parseInt(minutes) : minutes;

        if (isNaN(totalMinutes) || totalMinutes < 0) return '0m';

        const days = Math.floor(totalMinutes / (24 * 60));
        const remainingMinutesAfterDays = totalMinutes % (24 * 60);
        const hours = Math.floor(remainingMinutesAfterDays / 60);
        const remainingMinutes = remainingMinutesAfterDays % 60;

        const parts = [];

        if (days > 0) {
            parts.push(`${days}d`);
        }

        if (hours > 0) {
            parts.push(`${hours}h`);
        }

        if (remainingMinutes > 0 || parts.length === 0) {
            parts.push(`${remainingMinutes}m`);
        }

        return parts.join(' ');
    };

    /**
     * Formats a value (in pennies) to display as currency.
     * Example: 79937 => "$799.37"
     * @param value The value in pennies (smallest currency unit)
     * @param currency The currency code (default: 'USD')
     * @param locale The locale for formatting (default: 'en-US')
     * @returns Formatted currency string
     */
    const formatCurrency = (
        value: number | string | null | undefined,
        currency: string = 'USD',
        locale: string = 'en-US'
    ): string => {
        if (value === null || value === undefined || value === '') return '$0.00';

        const number = typeof value === 'string' ? parseFloat(value) : value;

        if (isNaN(number)) return '$0.00';

        // Convert from pennies to dollars
        const amount = number / 100;

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    /**
     * Formats an address object into a multi-line string.
     * Example: { line_1: "12020 Leeward Walk", line_2: "Suite 100", city: "Alpharetta", region: "GA", postal_code: "30005", country: "US" }
     * Returns:
     * 12020 Leeward Walk
     * Suite 100
     * Alpharetta, GA 30005 US
     * @param address The address object to format
     * @returns Formatted address string with line breaks
     */
    const formatAddress = (address: any): string => {
        if (!address) return 'N/A';

        const lines = [];

        // First line: street address
        if (address.line_1) {
            lines.push(address.line_1);
        }

        // Second line: line 2 if exists
        if (address.line_2) {
            lines.push(address.line_2);
        }

        // Third line: city, state, zip, country
        const locationParts = [address.city, address.region, address.postal_code, address.country].filter(Boolean);

        if (locationParts.length > 0) {
            lines.push(locationParts.join(', '));
        }

        return lines.join('\n');
    };

    /**
     * Formats a phone number with country code into a readable format.
     * Supports international phone numbers with proper formatting.
     * Examples:
     * - { country_code: "1", phone_number: "5551234567" } => "+1 (555) 123-4567"
     * - { country_code: "44", phone_number: "2071234567" } => "+44 20 7123 4567"
     * - { country_code: "81", phone_number: "3123456789" } => "+81 3 1234 5678"
     * @param phoneNumber The phone number object with country_code and phone_number properties
     * @returns Formatted phone number string
     */
    const formatPhoneNumber = (phoneNumber: { country_code: string; phone_number: string } | null | undefined): string => {
        if (!phoneNumber || !phoneNumber.country_code || !phoneNumber.phone_number) {
            return 'N/A';
        }

        const { country_code, phone_number } = phoneNumber;

        // Remove any non-digit characters from phone number
        const cleanNumber = phone_number.replace(/\D/g, '');

        if (!cleanNumber) {
            return 'N/A';
        }

        // Format based on country code
        switch (country_code) {
            case '1': // US/Canada
                if (cleanNumber.length === 10) {
                    return `+1 (${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6)}`;
                } else if (cleanNumber.length === 11 && cleanNumber.startsWith('1')) {
                    return `+1 (${cleanNumber.slice(1, 4)}) ${cleanNumber.slice(4, 7)}-${cleanNumber.slice(7)}`;
                }
                break;

            case '44': // UK
                if (cleanNumber.length >= 10) {
                    const areaCode = cleanNumber.slice(0, 2);
                    const rest = cleanNumber.slice(2);
                    if (rest.length >= 8) {
                        return `+44 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                    }
                }
                break;

            case '81': // Japan
                if (cleanNumber.length >= 9) {
                    const areaCode = cleanNumber.slice(0, 1);
                    const rest = cleanNumber.slice(1);
                    if (rest.length >= 8) {
                        return `+81 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                    }
                }
                break;

            case '49': // Germany
                if (cleanNumber.length >= 10) {
                    const areaCode = cleanNumber.slice(0, 2);
                    const rest = cleanNumber.slice(2);
                    if (rest.length >= 8) {
                        return `+49 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                    }
                }
                break;

            case '33': // France
                if (cleanNumber.length >= 9) {
                    const areaCode = cleanNumber.slice(0, 1);
                    const rest = cleanNumber.slice(1);
                    if (rest.length >= 8) {
                        return `+33 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                    }
                }
                break;

            case '61': // Australia
                if (cleanNumber.length >= 9) {
                    const areaCode = cleanNumber.slice(0, 1);
                    const rest = cleanNumber.slice(1);
                    if (rest.length >= 8) {
                        return `+61 ${areaCode} ${rest.slice(0, 4)} ${rest.slice(4, 8)}`;
                    }
                }
                break;

            default:
                // Generic international format for other countries
                if (cleanNumber.length >= 7) {
                    return `+${country_code} ${cleanNumber.slice(0, Math.ceil(cleanNumber.length / 2))} ${cleanNumber.slice(
                        Math.ceil(cleanNumber.length / 2)
                    )}`;
                }
        }

        // Fallback: just show country code + number
        return `+${country_code} ${cleanNumber}`;
    };

    /**
     * Strips plus-addressing from an email (e.g. "chris+privacy@gmail.com" -> "chris@gmail.com").
     * Mirrors the normalization the API applies in CheckForPreviousBreachesAction before
     * querying HIBP, so the UI shows the email that was actually scanned.
     */
    const stripEmailPlusAddressing = (email: string): string => {
        if (!email) return email;
        return email.replace(/^([^+]+)\+[^@]+(@.+)$/, '$1$2');
    };

    /**
     * useNameInputValidation composable
     *
     * Provides event handlers to prevent numbers from being entered into name fields.
     *
     * Usage:
     *   import { useNameInputValidation } from '~/composables/useUtils';
     *   const { onNameKeydown, onNameInput } = useNameInputValidation(nameRefs);
     *
     *   <input @keydown="onNameKeydown" @input="onNameInput('firstName', $event)" />
     *
     * @param nameRefs An object with refs for firstName, middleName, lastName (or any fields you want to control)
     * @returns { onNameKeydown, onNameInput }
     */
    /**
     * Single event handler to allow only letters in input fields.
     * Use as: @keydown="letterOnlyInputHandler" @input="letterOnlyInputHandler"
     * @param event KeyboardEvent | InputEvent
     */
    function letterOnlyInputHandler(event: Event) {
        // Handle paste: prevent pasting non-letter content
        if (event.type === 'paste') {
            const e = event as ClipboardEvent;
            e.preventDefault();

            // Get pasted text
            const pastedText = e.clipboardData?.getData('text') || '';

            const filteredText = pastedText.replace(/[^\p{L} ]+/gu, '');

            // Insert filtered text at cursor position
            const input = event.target as HTMLInputElement;
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const currentValue = input.value;

            input.value = currentValue.slice(0, start) + filteredText + currentValue.slice(end);

            // Set cursor position after inserted text
            const newPosition = start + filteredText.length;
            input.setSelectionRange(newPosition, newPosition);

            // Trigger input event to update v-model
            input.dispatchEvent(new Event('input', { bubbles: true }));

            return;
        }

        // Handle keydown: block non-letter keys (except allowed control keys)
        if (event.type === 'keydown') {
            const e = event as KeyboardEvent;
            const allowed = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete',
                'Home',
                'End',
                'Shift',
                'Control',
                'Meta',
                'Alt',
            ];
            if (allowed.includes(e.key)) return;
            // Allow dead keys (for diacritic composition on macOS/Linux)
            if (e.key === 'Dead') return;
            if (/^[\p{L} ]$/u.test(e.key)) return;
            // Block everything else (numbers, special chars, etc.)
            e.preventDefault();
        }

        // Handle input: strip non-letters from value
        if (event.type === 'input') {
            const input = event.target as HTMLInputElement;
            const filtered = input.value.replace(/[^\p{L} ]+/gu, '');
            if (input.value !== filtered) {
                input.value = filtered;
            }
        }
    }

    /**
     * Formats a phone number by hiding the country code if it is a US number (+1) and the remainder is 10 digits.
     * The formatted output will be in the form (###) ###-####.
     *
     * @param phoneNumber - The phone number string, e.g., '+12345678900' or '1234567890'.
     * @returns The formatted phone number, or the original input if it does not match the US format.
     */
    function formatUSPhoneNumber(phoneNumber: string): string {
        // Don't format masked phone numbers - return as-is
        if (/\*{2,}/.test(phoneNumber)) {
            return phoneNumber;
        }

        // Remove all non-digit characters
        const digits = phoneNumber.replace(/\D/g, '');
        // Check for US country code
        if (digits.length === 11 && digits.startsWith('1')) {
            const area = digits.slice(1, 4);
            const prefix = digits.slice(4, 7);
            const line = digits.slice(7, 11);
            return `(${area}) ${prefix}-${line}`;
        }
        // If already 10 digits, assume US format
        if (digits.length === 10) {
            const area = digits.slice(0, 3);
            const prefix = digits.slice(3, 6);
            const line = digits.slice(6, 10);
            return `(${area}) ${prefix}-${line}`;
        }
        // Return original if not a US number
        return phoneNumber;
    }

    return {
        truncateText,
        toTitleCase,
        formatNumber,
        formatTimeFromMinutes,
        formatCurrency,
        formatAddress,
        formatPhoneNumber,
        isValidEmail,
        stripEmailPlusAddressing,
        letterOnlyInputHandler,
        formatUSPhoneNumber,
    };
}
